/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import { EditorMainContext } from "../contexts/EditorMainContext";
import { Editor } from "../../Editor";
import { MainToolbar } from "./MainToolbar";
import { ViewProvider } from "./View/ViewProvider";
import { DialogProvider } from "./Dialogs/DialogProvider";
import { ToastProvider } from "./Toasts/ToastProvider";
import { ErrorBoundary } from "@adapptio/lib-components";
import { ErrorFallback } from "./ErrorFallback";
import { EditorUIContextProvider, useEventListener } from "@adapptio/lib-editor-ui";
import {
	IPreviewResolutionProviderProps,
	IPreviewResolutionSettings,
	PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY,
	PreviewResolutionProvider
} from "../../previewResolution";
import { isFunction, isNonEmptyArray } from "@adapptio/lib-shared";
import { DATA_FORMAT, DATA_FORMAT_COMPAT, METADATA } from "../../enums";
import { deserializeCopyPasteData, ICopyData, serializeCopyPasteData } from "../../copyPaste";
import { ReferenceSelectorContextProvider } from "../contexts/ReferenceSelectorContext";
import { useKeyBindings, useStateWithEvent } from "../hooks";

/**
 * Component props
 */
export interface IEditorMainProps {
	editorInstance: Editor;
}

/**
 *  Editor Main Component
 *
 * @param props Component props
 */
export const EditorMain: React.FunctionComponent<IEditorMainProps> = ({ editorInstance }) => {
	const rootElRef = useRef<HTMLDivElement>();

	const appInfo = editorInstance.get("appInfo");
	const resourceService = editorInstance.get("resourceService");
	const viewService = editorInstance.get("viewService");
	const abilityService = editorInstance.get("abilityService");
	const commandsService = editorInstance.get("commandsService");
	const deploymentService = editorInstance.get("deploymentService");
	const scmService = editorInstance.get("scmService");
	const dialogService = editorInstance.get("dialogService");
	const toastService = editorInstance.get("toastService");
	const menuService = editorInstance.get("menuService");
	const keyBindingService = editorInstance.get("keyBindingService");
	const runtimeService = editorInstance.get("runtimeService");
	const environmentService = editorInstance.get("environmentService");
	const stateService = editorInstance.get("stateService");
	const productSupportService = editorInstance.get("productSupportService");

	const previewResolutionSettings = useStateWithEvent(stateService?.onStateChanged, () => {
		return stateService?.get<IPreviewResolutionSettings>(PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY);
	});

	const savePreviewResolutionSettings = useCallback<
		IPreviewResolutionProviderProps["onPreviewResolutionSettingsChange"]
	>((settings) => {
		stateService?.set(PREVIEW_RESOLUTION_SETTINGS_STORAGE_KEY, settings);
	}, []);

	const userInfo = useStateWithEvent(environmentService?.onSessionChange, () =>
		environmentService?.getIdentity()
	);

	useKeyBindings(keyBindingService);

	useEventListener(window, "copy", (event: ClipboardEvent) => {
		const windowSelection = window.getSelection();

		if (windowSelection?.type === "Range") {
			return;
		}

		const currentView = viewService.getCurrentView();

		const metadata = Reflect.getMetadata(METADATA.ON_COPY_DATA, currentView);

		if (isNonEmptyArray(metadata)) {
			for (let i = 0; i < metadata.length; i++) {
				const handlerKey = metadata[i] as keyof typeof currentView;
				const handler = currentView[handlerKey];

				if (isFunction(handler)) {
					const data = handler.call(currentView, event) as ICopyData;
					event.clipboardData.setData(DATA_FORMAT.COPY_PASTE, serializeCopyPasteData(data));
					event.clipboardData.setData(DATA_FORMAT_COMPAT.HAE_COPY_PASTE, serializeCopyPasteData(data));
				}
			}
		}

		if (event.clipboardData.types.length) {
			event.preventDefault();
		}
	});

	useEventListener(window, "paste", (event: ClipboardEvent) => {
		if (!event.clipboardData.types.includes(DATA_FORMAT.COPY_PASTE) && !event.clipboardData.types.includes(DATA_FORMAT_COMPAT.HAE_COPY_PASTE)) {
			return;
		}

		event.preventDefault();

		const serializedData = event.clipboardData.getData(DATA_FORMAT.COPY_PASTE) || event.clipboardData.getData(DATA_FORMAT_COMPAT.HAE_COPY_PASTE);

		const data = deserializeCopyPasteData(serializedData);

		if (Object.keys(data).length > 0) {
			const currentView = viewService.getCurrentView();
			const metadata = Reflect.getMetadata(METADATA.ON_PASTE_DATA, currentView);

			if (isNonEmptyArray(metadata)) {
				for (let i = 0; i < metadata.length; i++) {
					const handlerKey = metadata[i] as keyof typeof currentView;
					const handler = currentView[handlerKey];

					if (isFunction(handler)) {
						handler.call(currentView, data);

						if (!event.defaultPrevented) {
							event.preventDefault();
						}
					}
				}
			}
		}
	});

	return (
		<ErrorBoundary fallback={<ErrorFallback />}>
			<div className="adpt-editor adpt-editor__main" ref={rootElRef}>
				<EditorMainContext.Provider
					value={{
						rootEl: rootElRef,
						appInfo: appInfo,
						userInfo: userInfo,
						abilityService: abilityService,
						commandsService: commandsService,
						keyBindingService: keyBindingService,
						menuService: menuService,
						runtimeService: runtimeService,
						resourceService: resourceService,
						stateService: stateService,
						toastService: toastService
					}}
				>
					<ReferenceSelectorContextProvider>
						<PreviewResolutionProvider
							previewResolutionSettings={previewResolutionSettings}
							onPreviewResolutionSettingsChange={savePreviewResolutionSettings}
						>
							<EditorUIContextProvider rootEl={rootElRef}>
								<MainToolbar
									appInfo={appInfo}
									resourceService={resourceService}
									deploymentService={deploymentService}
									scmService={scmService}
									viewService={viewService}
									productSupportService={productSupportService}
								/>
								<ViewProvider viewService={viewService} />
								<DialogProvider dialogService={dialogService} />
								<ToastProvider toastService={toastService} />
							</EditorUIContextProvider>
						</PreviewResolutionProvider>
					</ReferenceSelectorContextProvider>
				</EditorMainContext.Provider>
			</div>
		</ErrorBoundary>
	);
};
